import { differenceInCalendarDays } from "date-fns";

/**
 * Calculates days until the next occurrence of a celebration date.
 *
 * Important timezone handling notes:
 * - Celebration dates are stored in MySQL without time components
 * - They are transmitted as UTC dates, but we only care about date components (month/day/year)
 * - Calculations are done relative to the user's local timezone
 * - We strip time components to ensure midnight-to-midnight day calculations
 *
 * @param celebrationDate - The celebration date (stored as UTC but only date components matter)
 * @param targetDate - The reference date to calculate from (defaults to now)
 * @returns Number of calendar days until the celebration
 */
export const calculateLocalizedDaysUntilCelebration = ({
  celebrationDate,
  targetDate = new Date(),
}: {
  celebrationDate: Date;
  targetDate?: Date;
}): number => {
  // Date objects from an API are generally formatted as string representations
  // of the date so we need to convert them to Date objects
  let celebrationMonth, celebrationDay;

  if (celebrationDate instanceof Date) {
    celebrationMonth = celebrationDate.getUTCMonth();
    celebrationDay = celebrationDate.getUTCDate();
  } else {
    const dateObj = new Date(celebrationDate);
    celebrationMonth = dateObj.getUTCMonth();
    celebrationDay = dateObj.getUTCDate();
  }

  const localizedCelebrationDate = new Date(
    targetDate.getFullYear(),
    celebrationMonth,
    celebrationDay,
  );

  const localizedTargetDate = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    targetDate.getDate(),
  );

  const hasCelebrationHappenedThisYear =
    localizedCelebrationDate < localizedTargetDate;

  if (hasCelebrationHappenedThisYear) {
    localizedCelebrationDate.setFullYear(targetDate.getFullYear() + 1);
  }

  return differenceInCalendarDays(
    localizedCelebrationDate,
    localizedTargetDate,
  );
};

export const getAnniversaryYear = (celebrationDate: Date) => {
  const today = new Date();
  const celebration =
    celebrationDate instanceof Date
      ? celebrationDate
      : new Date(celebrationDate);

  // Clear time components for consistent day comparison
  const todayWithoutTime = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  const startDate = new Date(
    celebration.getFullYear(),
    celebration.getMonth(),
    celebration.getDate(),
  );

  // Calculate days until the next anniversary
  const daysUntilAnniversary = calculateLocalizedDaysUntilCelebration({
    celebrationDate: startDate,
    targetDate: todayWithoutTime,
  });

  // Calculate complete years between the dates
  let years = todayWithoutTime.getFullYear() - startDate.getFullYear();

  // Adjust if we haven't reached the anniversary day yet this year
  const anniversaryThisYear = new Date(
    todayWithoutTime.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
  );

  if (todayWithoutTime < anniversaryThisYear) {
    years--;
  }

  // Handle the case for upcoming first anniversaries
  if (years === 0 && daysUntilAnniversary <= 7) {
    // If it's within the first year and the anniversary is within a week,
    // show it as the 1st anniversary
    return 1;
  }

  // Ensure we never show "0th Anniversary"
  return Math.max(1, years);
};

export const formatDaysUntilCelebration = (
  days: number,
  type: string,
  celebrationDate: Date,
) => {
  // Only show celebrations that are happening soon (today, tomorrow, or within next 14 days)
  const MAX_DAYS_THRESHOLD = 14;

  if (days > MAX_DAYS_THRESHOLD) {
    return null; // Don't show celebrations too far in the future
  }

  const timePhrase =
    days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;

  if (type === "Anniversary") {
    const years = getAnniversaryYear(celebrationDate);
    return `${years}${getOrdinalSuffix(years)} ${type} is ${timePhrase}`;
  }

  return `${type} is ${timePhrase}`;
};

export const getOrdinalSuffix = (num: number) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return "st";
  if (j === 2 && k !== 12) return "nd";
  if (j === 3 && k !== 13) return "rd";
  return "th";
};

export const formatTimeFromDate = (date: Date): string => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";

  hours = hours % 12;
  hours = hours ? hours : 12;

  // pad minutes with leading zero if needed
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

  return `${hours}:${minutesStr}${ampm}`;
};

export function unixTimestampToDateString(unixTimestamp: number) {
  const date = new Date(unixTimestamp * 1000);

  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}
